import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Stack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { TextEllipsis } from 'components/TextEllipsis';
import { Modal } from 'components/Modal';
import { PaginatedTable } from 'components/PaginatedTable';
import { useCallback, useMemo, useState } from 'react';
import { Column } from 'react-table';
import { QueryState } from 'types/queryState';
import { useLayoutPaginatedTable } from 'hooks/useLayoutPaginatedTable';
import { useConfirmationDialog } from 'hooks/useConfirmationDialog';
import { useListInvitations } from 'queries/admin/useListInvitations';
import { Invitation } from 'types/invitation';
import { useInviteUser } from 'mutations/admin/useInviteUser';
import { useRevokeInvitation } from 'mutations/admin/useRevokeInvitation';
import { formatDateTime } from 'utils/dateUtils';
import { ButtonGroup } from 'components/ButtonGroup';
import { useResendInvitation } from 'mutations/admin/userResendInvitation';

const InvitationModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const { mutateAsync: sendInvitation, isLoading: isInviting } =
    useInviteUser();

  const sendInvitationHandler = useCallback(() => {
    sendInvitation(
      {
        emailAddress: email,
      },
      { onSuccess: onClose }
    );
  }, [email, onClose, sendInvitation]);

  return (
    <Modal
      title="Invite User"
      isOpen={isOpen}
      onClose={onClose}
      size={['full', null, '6xl']}
      showCloseButton
      buttons={[
        {
          label: 'Send Invitation',
          handler: sendInvitationHandler,
          isDisabled: isInviting,
        },
      ]}
    >
      <FormControl>
        <FormLabel>Email address</FormLabel>
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          readOnly={isInviting}
        />
      </FormControl>
    </Modal>
  );
};

const InvitationsTable: React.FC = () => {
  const { getColumnWidth } = useLayoutPaginatedTable(0);
  const columnPercentages = useBreakpointValue(
    {
      base: [30, 20, 20, 30],
    },
    { fallback: 'base' }
  )!;

  const invitationsResult = useListInvitations({ refetchOnMount: false });

  const { mutateAsync: deleteInvitation, isLoading: isRevoking } =
    useRevokeInvitation();

  const { mutateAsync: resendInvitation, isLoading: isResending } =
    useResendInvitation();

  const [createInvitationOpen, setCreateInvitationOpen] = useState(false);
  const { openConfirmationDialog } = useConfirmationDialog();

  const handleDelete = useCallback(
    (invitationId: string) => {
      openConfirmationDialog({
        title: 'Cancel Invitation',
        message: `Are you sure you want to cancel this invitation?`,
        onConfirm: () => deleteInvitation({ id: invitationId }),
        blockScrollOnMount: false,
        cancelButtonLabel: 'No',
        confirmButtonLabel: 'Yes',
      });
    },
    [deleteInvitation, openConfirmationDialog]
  );

  const handleResend = async (invitationId: string) => {
    await resendInvitation({ id: invitationId });
  };

  const handleInviteUser = useCallback(() => {
    setCreateInvitationOpen(true);
  }, [setCreateInvitationOpen]);

  const handleCloseInvite = useCallback(() => {
    setCreateInvitationOpen(false);
    invitationsResult.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setCreateInvitationOpen, invitationsResult.refetch]);

  const invitationsActionButtons = [
    {
      label: 'Resend Invite',
      variant: 'blueActionButton',
      handler: handleResend,
      tooltip: 'Recreated the invitation and sends it to the user',
    },
    {
      label: `Copy Invite URL`,
      variant: 'outline',
      tooltip: 'Copies the invitation URL to the clipboard',
    },
    {
      label: 'Delete Invite',
      variant: 'outline',
      handler: handleDelete,
      tooltip: 'Deletes the invitation',
    },
  ];

  const adminInvitationsColumns = useMemo(
    () =>
      [
        {
          Header: 'Email',
          accessor: 'emailAddress',
          width: getColumnWidth(columnPercentages[0]),
          Cell: ({ value: email_address }: { value: string }) => (
            <TextEllipsis
              display="flex"
              alignItems="center"
              h="100%"
              maxW="100%"
            >
              {email_address}
            </TextEllipsis>
          ),
        },
        {
          Header: 'Created At',
          accessor: 'createdAt',
          width: getColumnWidth(columnPercentages[1]),
          Cell: ({ value: created_at }: { value: string }) => (
            <TextEllipsis
              display="flex"
              alignItems="center"
              h="100%"
              maxW="100%"
            >
              {formatDateTime(created_at, 'MM/DD/YYYY', 'HH:MM AM/PM')}
            </TextEllipsis>
          ),
        },
        {
          Header: 'Expires At',
          accessor: 'expiresAt',
          width: getColumnWidth(columnPercentages[2]),
          Cell: ({ value: expires_at }: { value: string }) => (
            <TextEllipsis
              display="flex"
              alignItems="center"
              h="100%"
              maxW="100%"
            >
              {formatDateTime(expires_at, 'MM/DD/YYYY', 'HH:MM AM/PM')}
            </TextEllipsis>
          ),
        },
        {
          Header: 'Actions',
          accessor: 'id',
          width: getColumnWidth(columnPercentages[3]),
          Cell: (props) => (
            <ButtonGroup
              buttons={invitationsActionButtons.map((button) => ({
                ...button,
                handler: button.label.startsWith('Copy')
                  ? () => {
                      navigator.clipboard.writeText(
                        props.row.original.invitationUrl
                      );
                    }
                  : button.handler
                  ? () => {
                      button.handler(props.row.original.id);
                    }
                  : () => {
                      console.log('No handler');
                    },
              }))}
              wrapperProps={{
                display: ['flex', null, 'flex'],
              }}
              commonButtonWrapperProps={{
                mr: 0,
              }}
              commonButtonProps={{
                size: 'md',
                width: 'auto',
                height: '2.2rem',
                padding: '5px',
                whiteSpace: 'normal',
                fontSize: ['0.6rem', null, '0.8rem'],
              }}
            />
          ),
        },
      ] as Column<any>[],
    // eslint-disable-next-line
    [getColumnWidth, columnPercentages]
  );

  const isLoading =
    invitationsResult.isLoading ||
    invitationsResult.isFetching ||
    isRevoking ||
    isResending;

  return (
    <>
      <Stack>
        <Button
          onClick={handleInviteUser}
          fontSize="md"
          variant="redSquareButton"
          alignSelf="flex-end"
          rounded="md"
          mt={4}
        >
          Invite User
        </Button>
        <PaginatedTable
          columns={adminInvitationsColumns}
          queryState={
            {
              data: isLoading ? [] : invitationsResult.data || [],
              isLoading,
            } as QueryState<Invitation>
          }
          tableName="admin_users_table"
          isResizable
        />
      </Stack>
      <InvitationModal
        isOpen={createInvitationOpen}
        onClose={handleCloseInvite}
      />
    </>
  );
};

export default InvitationsTable;
